import AcknowledgementNotice from 'components/AcknowledgementNotice/AcknowledgementNotice';
import { X, UploadCloud, FileText } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo } from 'react';
import styles from './FileUpload.module.scss';
import { isDateValid } from 'helpers/appointments/appointments';
import { useDropzone } from 'react-dropzone';

import {
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  Paper,
  Typography,
  Alert
} from '@mui/material';

const sanitizeHTML = html => {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.innerHTML;
};

const FileUpload = ({
  files = [],
  onFilesChange,
  accept = {
    'image/png': ['.png'],
    'image/jpeg': ['.jpeg'],
    'image/jpg': ['.jpg'],
    'application/pdf': ['.pdf']
  },
  multiple = false,
  maxSize = 20 * 1024 * 1024,
  title = '',
  description = '',
  numberOfFiles = 1,
  fileIndex = null,
  url = null,
  transferNote = null,
  required = false,
  expirationDate = null,
  disabled = false,
  acknowledgementMessage = '',
  specialAcknowledgement = false,
  setSpecialAcknowledgement = () => {}
}) => {
  const acceptMessage =
    accept &&
    Object?.keys(accept)?.length > 0 &&
    Object.keys(accept)
      .map(key => accept[key].join(', '))
      .join(', ');

  // calculate if we should show error state
  const isError = useMemo(() => {
    // if required, we need files
    if (required) {
      return !files || files.length === 0;
    }

    // if we have a date, we need to validate it and make sure we have files
    if (expirationDate) {
      const dateValid = !!expirationDate
        ? (() => {
            const { $d: date } = expirationDate;
            return isDateValid(date).valid;
          })()
        : false;

      const hasFiles = files && files.length > 0;

      // error if we have a date but no files, or files but invalid date
      return (dateValid && !hasFiles) || (hasFiles && !dateValid);
    }

    return false;
  }, [required, files, expirationDate, isDateValid]);

  const onDrop = useCallback(
    (acceptedFiles, rejectedFiles) => {
      const currentFiles = files || [];

      const mappedAccepted = acceptedFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file)
      }));

      const isDuplicate = newFile =>
        currentFiles.some(
          existingFile =>
            existingFile.name === newFile.file.name &&
            existingFile.size === newFile.file.size
        );

      const uniqueAcceptedFiles = mappedAccepted.filter(
        fileWrapper => !isDuplicate(fileWrapper)
      );

      const newFiles = multiple
        ? [...currentFiles, ...uniqueAcceptedFiles.map(wrapper => wrapper.file)]
        : uniqueAcceptedFiles.length > 0
        ? [uniqueAcceptedFiles[uniqueAcceptedFiles.length - 1].file]
        : currentFiles;

      const rejectedErrorMessages = rejectedFiles.map(({ file, errors }) => {
        let errorMessage = errors[0]?.message;
        if (errorMessage && errorMessage.includes('bytes')) {
          errorMessage = `File size exceeds the limit of ${
            maxSize / 1024 / 1024
          }MB`;
        } else if (!errorMessage) {
          errorMessage = 'File rejected';
        }
        return `${file.name}: ${errorMessage}`;
      });
      if (rejectedErrorMessages.length > 0) {
        console.error('File upload errors:', rejectedErrorMessages);
      }

      onFilesChange && onFilesChange(fileIndex, newFiles);
    },
    [files, onFilesChange, multiple, maxSize, fileIndex]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    multiple,
    maxSize
  });

  const removeFile = useCallback(
    index => {
      const newFiles = files.filter((_, i) => i !== index);
      onFilesChange && onFilesChange(fileIndex, newFiles);
    },
    [files, onFilesChange, fileIndex]
  );

  const fileWrappers = useMemo(() => {
    return files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
  }, [files]);

  useEffect(() => {
    return () => {
      fileWrappers.forEach(wrapper => URL.revokeObjectURL(wrapper.preview));
    };
  }, [fileWrappers]);

  // determine error messages
  const errorMessage = useMemo(() => {
    if (required && (!files || files.length === 0)) {
      return `${numberOfFiles} file${numberOfFiles > 1 ? 's' : ''} need${
        numberOfFiles > 1 ? '' : 's'
      } to be uploaded.`;
    }

    if (expirationDate) {
      const dateValid = !!expirationDate
        ? (() => {
            const { $d: date } = expirationDate;
            return isDateValid(date).valid;
          })()
        : false;

      const hasFiles = files && files.length > 0;

      if (hasFiles && !dateValid) {
        return 'Please provide a valid expiration date.';
      }

      if (dateValid && !hasFiles) {
        return `${numberOfFiles} file${numberOfFiles > 1 ? 's' : ''} need${
          numberOfFiles > 1 ? '' : 's'
        } to be uploaded.`;
      }
    }

    return `${numberOfFiles} file${numberOfFiles > 1 ? 's' : ''} need${
      numberOfFiles > 1 ? '' : 's'
    } to be uploaded.`;
  }, [required, files, expirationDate, isDateValid, numberOfFiles]);

  return (
    <FormControl
      error={isError}
      style={{ marginBottom: '1rem' }}
      sx={{ width: '100%' }}
    >
      {!!transferNote && (
        <Alert
          severity='info'
          className={styles.transferNoteContainer}
          sx={{ mb: 2, mt: title ? 1 : 0 }}
        >
          <div
            className={styles.transferNoteContent}
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(transferNote) }}
          />
        </Alert>
      )}

      {!!url && (
        <Box mt={2} mb={2}>
          <Typography className={styles.downloadTitle} variant='body2'>
            Step 1: Download the file template
          </Typography>
          <a
            href={url}
            download
            target='_blank'
            rel='noopener noreferrer'
            className={styles.downloadLink}
          >
            Download file
          </a>
        </Box>
      )}

      {title?.includes('State Based Certificate') && (
        <Alert
          severity='info'
          className={styles.transferNoteContainer}
          sx={{ mb: 2, mt: title ? 1 : 0 }}
        >
          <div className={styles.transferNoteContent}>
            If you need to obtain a state-based certification, please refer to
            our ACA Map:{' '}
            <a
              href='https://oneillmarketing.net/aca/'
              target='_blank'
              rel='noreferrer'
            >
              https://oneillmarketing.net/aca/
            </a>
          </div>
        </Alert>
      )}

      {title && (
        <Box className={styles.titleContainer}>
          <Typography
            variant='body1'
            className={`${styles.title} ${
              required ? styles.requiredField : ''
            }`}
          >
            {!!url && 'Step 2: '}
            {title}
          </Typography>
          <span
            className={`${styles.fileRequirementBadge} ${
              required ? styles.required : styles.optional
            }`}
          >
            {required ? 'Required' : 'Optional'}
          </span>
        </Box>
      )}

      <Box
        className={`${styles.uploadContainer} ${isError ? styles.error : ''} ${
          required && (!files || files.length === 0)
            ? styles.requiredUpload
            : ''
        } ${disabled ? styles.disabled : ''}`}
        elevation={0}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <Box className={styles.uploadBox}>
          <UploadCloud size={24} className={styles.uploadIcon} />
          <Typography variant='body2' className={styles.uploadText}>
            {isDragActive
              ? `Drop the ${multiple ? 'files' : 'file'} here...`
              : `Drag & drop ${
                  multiple ? 'files' : 'file'
                } here, or click to select`}
          </Typography>
        </Box>
        <Typography variant='body2' className={styles.infoText}>
          {acceptMessage && `Only ${acceptMessage} files are allowed.`}{' '}
          {`Max size of ${maxSize / 1024 / 1024}MB.`}
        </Typography>
        <Box>
          {fileWrappers.map((fileWrapper, index) => (
            <Paper key={index} className={styles.fileItem} elevation={2}>
              <Box className={styles.fileInfo}>
                {fileWrapper.preview ? (
                  <FileText size={20} />
                ) : (
                  <FileText size={20} style={{ color: '#d32f2f' }} />
                )}
                <Typography variant='body2' className={styles.fileName}>
                  {fileWrapper.file.name.length > 20
                    ? `${fileWrapper.file.name.slice(
                        0,
                        17
                      )}...${fileWrapper.file.name.split('.').pop()}`
                    : fileWrapper.file.name}
                </Typography>
              </Box>
              <IconButton
                onClick={e => {
                  if (disabled) return;
                  e.stopPropagation();
                  removeFile(index);
                }}
                className={styles.removeButton}
              >
                <X size={20} style={{ color: '#d32f2f' }} />
              </IconButton>
            </Paper>
          ))}
        </Box>
        {isError && (
          <FormHelperText error={isError}>{errorMessage}</FormHelperText>
        )}
      </Box>
      {description && (
        <FormHelperText
          className={acknowledgementMessage ? styles.description : ''}
        >
          <div
            dangerouslySetInnerHTML={{ __html: sanitizeHTML(description) }}
          />
        </FormHelperText>
      )}
      {acknowledgementMessage && (
        <AcknowledgementNotice
          title={`Step ${!url ? '2' : '3'}: Required Acknowledgement`}
          message={acknowledgementMessage}
          isError={!specialAcknowledgement}
          checked={specialAcknowledgement}
          onChange={setSpecialAcknowledgement}
          variant='info'
          addBottomMargin={false}
        />
      )}
    </FormControl>
  );
};

FileUpload.propTypes = {
  files: PropTypes.array,
  onFilesChange: PropTypes.func,
  accept: PropTypes.object,
  multiple: PropTypes.bool,
  maxSize: PropTypes.number,
  title: PropTypes.string,
  description: PropTypes.string,
  numberOfFiles: PropTypes.number,
  fileIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  url: PropTypes.string,
  transferNote: PropTypes.string,
  required: PropTypes.bool,
  expirationDate: PropTypes.object,
  disabled: PropTypes.bool,
  acknowledgementMessage: PropTypes.string,
  specialAcknowledgement: PropTypes.bool,
  setSpecialAcknowledgement: PropTypes.func
};

export default FileUpload;
